// src/pages/LegalPage.jsx
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import styles from './LegalPage.module.css';

export default function LegalPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [openSection, setOpenSection] = useState('privacy');

  const toggleSection = (section) => {
    setOpenSection(openSection === section ? null : section);
  };

  return (
    <div className={styles.page}>
      <header className={styles.topBar}>
        <button className={styles.logoBtn} onClick={() => navigate('/')} type="button">
          <span className={styles.logoBtnIcon}>D</span>
          <span className={styles.logoBtnText}>Delefy</span>
        </button>
        <button
          className={styles.backBtn}
          onClick={() => navigate(user ? '/profile' : '/')}
          type="button"
        >
          {user ? 'Profile' : 'Back to Login'}
        </button>
      </header>

      <main className={styles.main}>
        <div className={styles.header}>
          <h1 className={styles.title}>Legal & Privacy</h1>
          <p className={styles.subtitle}>
            Everything you need to know about how we protect your client data and your responsibilities as a user.
          </p>
        </div>

        <div className={styles.accordion}>
          {/* Privacy Policy Section */}
          <div className={`${styles.accordionItem} ${openSection === 'privacy' ? styles.active : ''}`}>
            <button 
              className={styles.accordionHeader} 
              onClick={() => toggleSection('privacy')}
              type="button"
            >
              <h2>Privacy Policy</h2>
              <span className={styles.icon}>{openSection === 'privacy' ? '−' : '+'}</span>
            </button>
            <div className={styles.accordionContent}>
              <div className={styles.textContent}>
                <h3>Zero Retention Architecture</h3>
                <p>
                  Delefy is built on a zero retention principle. The moment you upload a bill, bank statement or Tally Master file, it exists on our servers only for as long as it takes to extract, process and hand the results back to you. Nothing is stored for AI training, nothing is stored for our convenience and nothing is kept beyond what is strictly necessary.
                </p>
                <p>
                  Raw extracted data and original bill images are deleted immediately after processing is done and output is generated on Review &amp; Confirm Dashboard.
                </p>
                <p>
                  Deletion is our default. The only exception is a short 24 hour safety window that exists purely to protect you, not to retain your data. If you close the tab, lose connection or simply step away before clicking Start Over, we do not assume you are finished. We hold the finalised, structured values needed for your CSV (amounts, dates and ledger mappings only, never the raw extracted output or images) for a maximum of 24 hours so you can come back and complete your work without starting over. After 24 hours, this data is permanently and automatically erased, whether or not you have downloaded it.
                </p>
                <p>
                  The moment you click Start Over, we consider our purpose of processing for your specific job to be fully achieved and erase all your data from our servers instantly and irreversibly. There is no waiting period when you tell us you are done.
                </p>

                <h3>Your Client Ledgers</h3>
                <p>
                  Your Master.xml ledger map follows the same zero retention principle. It is used only to validate ledger names during the session it was uploaded for. The moment you refresh the page, log out or start a new session, it is gone. It is never retained across sessions and never used for anything other than the processing you initiated.
                </p>

                <h3>No Model Training on Client Data</h3>
                <p>
                  We do not use your clients' financial records, bills, bank statements or corrected fields to train, fine tune or improve our AI models. When you correct a field, we may log anonymised, non identifying metadata, for example that a date needed correcting or that a GSTIN was the wrong length, purely to improve extraction accuracy. We never log the actual values, names or numbers themselves.
                </p>

                <h3>No Sharing or Selling of Client Data</h3>
                <p>
                  We never sell, rent, share or otherwise disclose your clients' sensitive financial data to any third party, advertiser or data broker, under any circumstances. Your data is used solely to deliver the extraction and mapping service you requested, for the limited time outlined above, and for nothing else.
                </p>
              </div>
            </div>
          </div>

          {/* Terms of Service Section */}
          <div className={`${styles.accordionItem} ${openSection === 'terms' ? styles.active : ''}`}>
            <button 
              className={styles.accordionHeader} 
              onClick={() => toggleSection('terms')}
              type="button"
            >
              <h2>Terms of Service</h2>
              <span className={styles.icon}>{openSection === 'terms' ? '−' : '+'}</span>
            </button>
            <div className={styles.accordionContent}>
              <div className={styles.textContent}>
                <h3>Assistive Technology Only</h3>
                <p>
                  Delefy uses artificial intelligence to automate unstructured financial data extraction. Automated data parsing is inherently subject to error and misinterpretation. Delefy operates strictly as an assistive workspace tool, not a professional accounting or auditing substitute.
                </p>

                <h3>Human in the Loop Review Requirement</h3>
                <p>
                  You retain absolute, non delegable professional responsibility under ICAI guidelines for the accuracy of your books of accounts. You are solely responsible for validating and verifying all ledger mappings and extracted fields on the Review Dashboard before downloading the final CSV and importing it into TallyPrime or any other accounting system.
                </p>

                <h3>Data Availability Window</h3>
                <p>
                  Once your files finish processing, you have a 24 hour window to review, confirm and download your CSV, unless you click Start Over sooner, which ends this window immediately. If you do not download within this window, your data expires and is permanently erased. You will need to re upload and re process your files.
                </p>

                <h3>Who Is Responsible For What</h3>
                <p>
                  Under India's Digital Personal Data Protection Act, you as the Chartered Accountant are the Data Fiduciary for your clients' information. You decide why and how it is processed. Delefy acts strictly as your Data Processor, processing data only to deliver the service you requested and erasing it per the timelines above.
                </p>
                
                <h3>Absolute Limitation of Liability</h3>
                <p>
                  Under no circumstances shall Delefy, its parent platform RiJain One, its parent company RiJain, its founders or its affiliates be held liable for any statutory penalties, tax defaults, financial losses, audit failures, interest levies or professional malpractice claims resulting directly or indirectly from the deployment or use of the generated CSV files.
                </p>
              </div>
            </div>
          </div>

          {/* Refund & Cancellation Policy Section */}
          <div className={`${styles.accordionItem} ${openSection === 'refund' ? styles.active : ''}`}>
            <button 
              className={styles.accordionHeader} 
              onClick={() => toggleSection('refund')}
              type="button"
            >
              <h2>Refund &amp; Cancellation Policy</h2>
              <span className={styles.icon}>{openSection === 'refund' ? '−' : '+'}</span>
            </button>
            <div className={styles.accordionContent}>
              <div className={styles.textContent}>
                <h3>All Sales Are Final</h3>
                <p>
                  All purchases made on Delefy, including Plus Bundle credits, are final. We do not offer refunds, exchanges or cancellations once a purchase is completed, regardless of how many credits have been used.
                </p>

                <h3>Credit Validity</h3>
                <p>
                  Purchased credits do not expire through use. They remain valid and usable until fully consumed, with no monthly or annual usage deadline attached to the credits themselves.
                </p>

                <h3>Inactivity Forfeiture</h3>
                <p>
                  If an account remains inactive for a continuous period of 12 months, any remaining unused credits on that account will be forfeited. This exists to keep our records accurate and does not apply to accounts with any activity within the preceding 12 months.
                </p>

                <h3>Billing Errors</h3>
                <p>
                  If you believe a payment was made in error or a technical issue resulted in an incorrect charge, contact us at support.delefy@rijainone.com within 7 days of the transaction. We will review such cases individually.
                </p>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}